/**
 * PlotTrack API (#817). The grid is the writer-facing outline door.
 * Uses the #808 store and #809 map. No ready field. No Scene Spec.
 */
import { api } from './client';
import { notifyNoteChanged } from '../utils/noteChanged';

const unwrap = (res) => (res?.data?.data ?? res?.data ?? res);

/** Free, before Generate spends: sequences without talk, Book Notes, last check. */
export async function getPlotTrackGeneratePrecheck(storyId) {
  return unwrap(await api.get(`/stories/${storyId}/plottrack/generate/precheck`));
}

export async function checkPlotTrackOutline(storyId) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/check-outline`, {}));
}

export async function mapPlotTrack(storyId, { signal, timeoutMs } = {}) {
  const options = {};
  if (signal) options.signal = signal;
  if (timeoutMs) options.timeoutMs = timeoutMs;
  if (signal || timeoutMs) {
    return unwrap(await api.post(`/stories/${storyId}/plottrack/map`, {}, options));
  }
  return unwrap(await api.post(`/stories/${storyId}/plottrack/map`));
}

export async function generatePlotTrack(storyId, {
  replace = false,
  use_notes = false,
  accept = false,
  assess_only = false,
  apply_mode = null,
  plan = null,
  fight_resolutions = null,
  continue_incomplete = false,
  force_accept_ungrounded = false,
  confirm_prose_loss = false,
  steer_note = '',
  part_name = '',
  signal,
} = {}) {
  const body = {
    replace,
    use_notes,
    accept,
    assess_only,
    continue_incomplete,
    force_accept_ungrounded,
  };
  if (steer_note) body.steer_note = steer_note;
  if (part_name) body.part_name = part_name;
  if (apply_mode) body.apply_mode = apply_mode;
  if (plan) body.plan = plan;
  if (fight_resolutions) body.fight_resolutions = fight_resolutions;
  if (confirm_prose_loss) body.confirm_prose_loss = true;
  if (signal) {
    return unwrap(await api.post(`/stories/${storyId}/plottrack/generate`, body, { signal }));
  }
  return unwrap(await api.post(`/stories/${storyId}/plottrack/generate`, body));
}

export async function setPlotTrackWhoseHead(storyId, sceneId, {
  choice,
  entityId,
  bibleEntryId,
} = {}) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/scenes/${sceneId}/whose-head`, {
    choice,
    entity_id: entityId || null,
    bible_entry_id: bibleEntryId || null,
  }));
}

export async function upsertPlotTrackCell(storyId, { rowId, columnId, state, note, beats }) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/cells`, {
    row_id: rowId,
    column_id: columnId,
    state,
    note: note || '',
    beats: beats || [],
  }));
}

export async function placePlotTrackScene(storyId, { sceneId, parentSectionId }) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/place`, {
    scene_id: sceneId,
    parent_section_id: parentSectionId,
  }));
}

export async function setPlotTrackHeaderSpan(storyId, { headerId, startSceneId, endSceneId }) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/headers/${headerId}/span`, {
    start_scene_id: startSceneId,
    end_scene_id: endSceneId,
  }));
}

export async function updatePlotTrackRow(storyId, rowId, body) {
  return unwrap(await api.patch(`/stories/${storyId}/plottrack/rows/${rowId}`, body));
}

// Existing overlay write path (DLA-CHAPTER-OVERLAY-003). Suggest persists
// nothing. Commit closes a chapter after a scene. PlotTrack remaps after
// commit so Chapter headers appear. Not a through-line / spine parent.
export async function suggestPlotTrackChapterBreaks(storyId) {
  const res = unwrap(await api.post(`/stories/${storyId}/chapters/suggest-breaks`, {}));
  return res?.suggestions || [];
}

export async function commitPlotTrackChapterBreak(storyId, afterSceneId) {
  return unwrap(await api.post(`/stories/${storyId}/chapters/commit-break`, {
    after_scene_id: afterSceneId ?? null,
  }));
}

// DELETE removes the divider. Scenes stay. Recently deleted does not
// keep chapter dividers; Undo re-commits the same break.
export async function clearPlotTrackChapterBreak(storyId, chapterId) {
  return unwrap(await api.delete(`/stories/${storyId}/chapters/${chapterId}`));
}

// Run 36: add one scene after another, no framework needed. No model call.
export async function addPlotTrackScene(storyId, { afterSceneId, whatHappens, title }) {
  const body = {
    what_happens: whatHappens,
    title: title || '',
  };
  if (afterSceneId) body.after_scene_id = afterSceneId;
  return unwrap(await api.post(`/stories/${storyId}/plottrack/scenes`, body));
}

// Run 48: rename a scene, or move it to the end of another sequence. No model call.
export async function editPlotTrackScene(storyId, sceneId, {
  title, sequenceId, afterSceneId, atStart, beats, rowId,
} = {}) {
  const body = {};
  if (title !== undefined) body.title = title;
  if (sequenceId) body.sequence_id = sequenceId;
  // Run 51: choose where it goes; neither means the end of the sequence.
  if (afterSceneId) body.after_scene_id = afterSceneId;
  if (atStart) body.at_start = true;
  // Save scene sends the beats it is showing. A card rename omits them.
  if (beats !== undefined) body.beats = beats;
  if (rowId) body.row_id = rowId;
  return unwrap(await api.patch(`/stories/${storyId}/plottrack/scenes/${sceneId}`, body));
}

// One small paid call; the caller confirms the cost first.
export async function suggestPlotTrackSceneTitle(storyId, whatHappens) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/scene-title`, {
    what_happens: whatHappens,
  }));
}

export async function createPlotTrackRow(storyId, { type, label, visibility, color, verticalOrder }) {
  const body = { type };
  if (label != null) body.label = label;
  if (visibility != null) body.visibility = visibility;
  if (color != null) body.color = color;
  if (verticalOrder != null) body.vertical_order = verticalOrder;
  return unwrap(await api.post(`/stories/${storyId}/plottrack/rows`, body));
}

// When labels (spec: docs/plottrack/plot-track-time-people-and-canon-room-spec.md §1).
// Run 53: use the timelines Generate named, for every scene without a When label.
export async function applyPlotTrackWhenTimelines(storyId) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/when-labels/apply-timelines`));
}

export async function createPlotTrackWhenLabel(storyId, label) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/when-labels`, { label }));
}

export async function renamePlotTrackWhenLabel(storyId, labelId, label) {
  return unwrap(await api.patch(`/stories/${storyId}/plottrack/when-labels/${labelId}`, { label }));
}

export async function reorderPlotTrackWhenLabels(storyId, labelIds) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/when-labels/order`, { label_ids: labelIds }));
}

export async function deletePlotTrackWhenLabel(storyId, labelId) {
  return unwrap(await api.delete(`/stories/${storyId}/plottrack/when-labels/${labelId}`));
}

export async function setPlotTrackSceneWhen(storyId, sceneId, labelId) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/scenes/${sceneId}/when`, {
    label_id: labelId || null,
  }));
}

export async function setPlotTrackScenePerson(storyId, sceneId, entryId, inScene) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/scenes/${sceneId}/people/${entryId}`, {
    in_scene: Boolean(inScene),
  }));
}

// Column width (spec §3).
export async function setPlotTrackColumnWidth(storyId, width) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/column-width`, { width }));
}

export async function resetPlotTrackColumnWidths(storyId) {
  return unwrap(await api.post(`/stories/${storyId}/plottrack/column-width/reset`, {}));
}

export async function listRecentlyDeleted(storyId) {
  return unwrap(await api.get(`/stories/${storyId}/plottrack/recently-deleted`));
}

export async function restoreRecentlyDeleted(storyId, kind, itemId) {
  const result = unwrap(await api.post(
    `/stories/${storyId}/plottrack/recently-deleted/${kind}/${itemId}/restore`,
    {},
  ));
  if (String(kind || '').toLowerCase() === 'note') notifyNoteChanged(storyId);
  return result;
}

export async function setPlotTrackOneColumnWidth(storyId, columnId, widthPx) {
  return unwrap(await api.put(`/stories/${storyId}/plottrack/columns/${columnId}/width`, {
    width_px: widthPx == null ? null : Math.round(widthPx),
  }));
}

export const plottrackApi = {
  checkOutline: checkPlotTrackOutline,
  map: mapPlotTrack,
  generate: generatePlotTrack,
  setWhoseHead: setPlotTrackWhoseHead,
  upsertCell: upsertPlotTrackCell,
  placeScene: placePlotTrackScene,
  setHeaderSpan: setPlotTrackHeaderSpan,
  updateRow: updatePlotTrackRow,
  createRow: createPlotTrackRow,
  addScene: addPlotTrackScene,
  suggestSceneTitle: async (storyId, whatHappens) => {
    const out = await suggestPlotTrackSceneTitle(storyId, whatHappens);
    return out?.title || '';
  },
  suggestChapterBreaks: suggestPlotTrackChapterBreaks,
  commitChapterBreak: commitPlotTrackChapterBreak,
  clearChapterBreak: clearPlotTrackChapterBreak,
};
